import React from 'react';
import { FiLock, FiCheck, FiStar, FiZap } from 'react-icons/fi';
import {
  FeatureLevel,
  getFeatureBadgeColor,
  getFeatureLevelLabel,
  FEATURE_DISPLAY_NAMES,
  FEATURE_DESCRIPTIONS,
  FeatureName
} from '../../types/features';

interface FeatureBadgeProps {
  feature: FeatureName;
  level: FeatureLevel;
  showLabel?: boolean;
  showDescription?: boolean;
  locked?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

/**
 * Composant pour afficher un badge de feature avec son niveau
 *
 * Usage:
 *   <FeatureBadge feature="cost_estimation" level="advanced" showLabel />
 *   <FeatureBadge feature="export_bicep" level={false} locked />
 */
const FeatureBadge: React.FC<FeatureBadgeProps> = ({
  feature,
  level,
  showLabel = true,
  showDescription = false,
  locked = false,
  size = 'md',
  onClick
}) => {
  const displayName = FEATURE_DISPLAY_NAMES[feature] || feature;
  const description = FEATURE_DESCRIPTIONS[feature];
  const badgeColor = locked ? 'bg-gray-100 text-gray-400' : getFeatureBadgeColor(level);

  // Déterminer l'icône selon le niveau
  const getIcon = () => {
    if (locked) return <FiLock className="inline" />;

    if (typeof level === 'boolean') {
      return level ? <FiCheck className="inline" /> : <FiLock className="inline" />;
    }

    switch (level) {
      case 'basic':
        return <span className="inline-block w-2 h-2 rounded-full bg-current" />;
      case 'standard':
        return <FiCheck className="inline" />;
      case 'advanced':
        return <FiStar className="inline" />;
      case 'unlimited':
        return <FiZap className="inline" />;
      default:
        return null;
    }
  };

  // Classes selon la taille
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const baseClasses = `inline-flex items-center gap-1.5 rounded-full font-medium ${badgeColor} ${sizeClasses[size]}`;
  const containerClasses = onClick ? `${baseClasses} cursor-pointer hover:opacity-80 transition-opacity` : baseClasses;

  const content = (
    <>
      {getIcon()}
      {showLabel && (
        <span>
          {displayName}
          {typeof level !== 'boolean' && level && (
            <span className="ml-1 opacity-75 text-xs">
              ({getFeatureLevelLabel(level)})
            </span>
          )}
        </span>
      )}
      {locked && <span className="text-xs opacity-75">Upgrade</span>}
    </>
  );

  if (showDescription && description) {
    return (
      <div className="group relative">
        <div className={containerClasses} onClick={onClick}>
          {content}
        </div>
        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
          <div className="bg-gray-900 text-white text-sm rounded-lg px-3 py-2 w-64 shadow-lg">
            <div className="font-semibold mb-1">{displayName}</div>
            <div className="text-gray-300 text-xs">{description}</div>
            {locked && (
              <div className="text-yellow-300 text-xs mt-2">
                🔒 Disponible à partir du plan STANDARD
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses} onClick={onClick}>
      {content}
    </div>
  );
};

export default FeatureBadge;
